// Where the season is right now, for the masthead dateline.
//
// Three pages carry the dateline and the answer is identical for all of them,
// so the request is made once per page load and shared. Cached at module
// scope rather than in context: it is one small public GET, it changes at most
// once a week, and threading a provider through the app shell for it would be
// more machinery than the fact deserves.
import { useEffect, useState } from 'react';
import apiService from '../services/api';
import { SeasonNow } from '../types';

let inFlight: Promise<SeasonNow> | null = null;

/** Clears the shared cache. Tests only. */
export function resetSeasonCache(): void {
  inFlight = null;
}

export function useSeasonNow(): SeasonNow | null {
  const [season, setSeason] = useState<SeasonNow | null>(null);

  useEffect(() => {
    let active = true;
    if (!inFlight) {
      inFlight = apiService.getCurrentSeason();
    }
    inFlight
      .then(s => { if (active) setSeason(s); })
      // A failed lookup is not worth an error state — the masthead falls back
      // to the brand line and the page behaves exactly as it did before.
      .catch(() => { inFlight = null; if (active) setSeason(null); });
    return () => { active = false; };
  }, []);

  return season;
}

/**
 * The masthead eyebrow: "WEEK 3 · 2026 SEASON" in season, the brand line out
 * of it. Never a half dateline — a week with no season, or a season with no
 * week, reads as broken rather than as off-season.
 */
export function datelineFor(season: SeasonNow | null): string {
  if (season && !season.is_offseason && season.week && season.season) {
    return `Week ${season.week} · ${season.season} Season`;
  }
  return 'The Special Teams Record Book';
}
